import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchProductDetail,
  clearProductDetail,
} from "../redux/productsQuickViewSlice";
import { addToCart } from "../../../Checkout/Cart/redux/cartSlice";
import { toast } from "react-toastify";
import {
  addToWishlist,
  removeFromWishlist,
} from "../../../../components/Wishlist/WishlistSlice";
import { checkPincode, resetPincodeState } from "../redux/pincodeSlice";
import { useNavigate, useLocation } from "react-router-dom";
import { useCartWishlist } from "../../../../app/CartWishlistContext";

function useQuickView({ product, show, onHide }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { getCartWishlistCount } = useCartWishlist();
  const imageRef = useRef(null);
  const modalRef = useRef(null);

  /* ------------------ STATE ------------------ */
  
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [localLoading, setLocalLoading] = useState(true);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [pincode, setPincode] = useState("");
  const [pincodeChecked, setPincodeChecked] = useState(false);
  const [pincodeDetails, setPincodeDetails] = useState({});
  const [productDetails, setProductsDetails] = useState(null);
  const [unit, setUnit] = useState("cm");
  const [wishlistLoading, setWishlistLoading] = useState({});

   /* ------------------ AUTH ------------------ */

  const token = localStorage.getItem("token");
  const isLoggedIn = !!token;


  /* ------------------ SLUG / SKU ------------------ */
  const slug = product?.slug || product?.action_url || null;
  const sku = product?.sku || null;

  /* ------------------ REDUX ------------------ */
  const { data, loading } = useSelector(
    (state) => state.quickView || { data: null, loading: false }
  );

  const { pinset, pinloading, pinerror } = useSelector(
    (state) => state.pincode
  );

  const collection = data?.collection || "";

  /* ------------------ FETCH PRODUCT ------------------ */
  useEffect(() => {
    if (!show) return;
    if (!slug && !sku) return;

    setLocalLoading(true);
    setSelectedImage(null);
    setSelectedSize(null);
    setSelectedColor(null);
    setSelectedMedia(null);
    setQuantity(1);

    dispatch(clearProductDetail());
    dispatch(fetchProductDetail({ slug, sku }));

    const storedPin = localStorage.getItem("pincode");
    if (storedPin) {
      setPincode(storedPin);
      dispatch(checkPincode(storedPin));
    }

  }, [dispatch, slug, sku, show]);

  /* ------------------ INIT PRODUCT ------------------ */
  useEffect(() => {
    if (!data?.product_sizes?.length) return;

    const firstSize = data.product_sizes[0];
    const firstColor = firstSize?.product_colors?.[0];
    const firstMedia = firstColor?.product_media?.[0]?.media || null;

    setProductsDetails(data);
    setSelectedSize(firstSize);
    setSelectedColor(firstColor);
    setSelectedImage(firstMedia);
    setSelectedMedia(firstMedia);

    const stock = firstColor?.avail_stock || 0;
    setQuantity(stock > 0 ? 1 : 0);

    setLocalLoading(false);

  }, [data]);

  /* ------------------ PINCODE ------------------ */
  useEffect(() => {
    if (pinset && pinset.pincode) {
      if (pinset.is_active) {
        setPincodeChecked(true);
        setPincodeDetails(pinset);

        // ✅ store only on success
        localStorage.setItem("pincode", pinset.pincode);
      } else {
        setPincodeChecked(false);
        setPincodeDetails({});
      }
    }
  }, [pinset]);

  /* ------------------ PINCODE ERROR ------------------ */
  useEffect(() => {
    if (pinerror) {
      setPincodeChecked(false);
      setPincodeDetails({});
    }
  }, [pinerror]);

  /* ------------------ PINCODE HANDLERS ------------------ */

  const handleCheck = () => {
    if (!pincode || pincode.length !== 6) {
      toast.error("Enter valid 6 digit pincode");
      return;
    }

    dispatch(checkPincode(pincode));
  };

  const handleReset = () => {
    dispatch(resetPincodeState());
    setPincode("");
    setPincodeChecked(false);
    setPincodeDetails({});
    localStorage.removeItem("pincode");
  };

  /* ------------------ IMAGE ZOOM ------------------ */
  const handleMouseMove = (e) => {
    if (!imageRef.current) return;

    const rect = e.currentTarget.getBoundingClientRect();

    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    imageRef.current.style.transformOrigin = `${x}% ${y}%`;
    imageRef.current.style.transform = "scale(1.8)";
  };

  const handleMouseLeave = () => {
    if (!imageRef.current) return;
    imageRef.current.style.transform = "scale(1)";
  };

  /* ------------------ MODAL ------------------ */
  const handleClose = () => {
    setPincode("");
    setPincodeChecked(false);
    setPincodeDetails({});
    onHide?.();
  };

  const handleOutsideClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      handleClose();
    }
  };


  /* ------------------ ADD TO CART ------------------ */

  const handleAddToCart = () => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login");
      return;
    }

    if (!pincodeChecked || !pincodeDetails?.is_active) {
      toast.error("Verify your pincode to delivery address");
      return;
    }

    if (quantity <= 0) {
      toast.error("Product out of stock");
      return;
    }

    dispatch(
      addToCart({
        product_id: selectedColor?.id,
        quantity,
      })
    )
      .unwrap()
      .then(() => {
        getCartWishlistCount();
        toast.success("Added to cart");
      })
      .catch(() => toast.error("Add to cart failed"));
  };

  /* ------------------ WISHLIST ------------------ */

     /* ------------------ WISHLIST ------------------ */
  const toggleWishlist = async (e, product) => {
    e.stopPropagation();
    if (!isLoggedIn) return navigate("/login", { state: { from: location.pathname } });
    if (!product) return;

    setWishlistLoading((prev) => ({ ...prev, [product.id]: true }));

    try {
      if (product.is_wishlisted) {
        const wishlistId = product.wishlist?.[0]?.wishlist_id;
        if (wishlistId) await dispatch(removeFromWishlist(wishlistId)).unwrap();
        setSelectedSize((prev) => ({ ...prev, is_wishlisted: false, wishlist: [] }));
      } else {
        const response = await dispatch(addToWishlist({ product_id: product.id })).unwrap();
        const wishlistItem = Array.isArray(response)
          ? response.find((w) => w.product_id === product.id)
          : response;
        setSelectedSize((prev) => ({
          ...prev,
          is_wishlisted: true,
          wishlist: wishlistItem ? [{ wishlist_id: wishlistItem.id }] : [],
        }));
      }
      getCartWishlistCount();
    } catch {
      toast.error("Wishlist action failed");
    } finally {
      setWishlistLoading((prev) => ({ ...prev, [product.id]: false }));
    }
  };


 



  /* ------------------ SIZE / COLOR ------------------ */
  const selectionColor = (color) => {
    setSelectedColor(color);

    const media = color?.product_media?.[0]?.media || null;
    setSelectedImage(media);

    const stock = color?.avail_stock || 0;
    setQuantity(stock > 0 ? 1 : 0);
  };

  const sizeSelection = (size) => {
    setSelectedSize(size);

    const color = size?.product_colors?.[0] || null;

    setSelectedColor(color);

    const media = color?.product_media?.[0]?.media || null;
    setSelectedImage(media);

    const stock = color?.avail_stock || 0;
    setQuantity(stock > 0 ? 1 : 0);
  };

  /* ------------------ PRICE ------------------ */
  const currentPrice = selectedSize?.price || data?.selling_price;
  const isPincodeLocked = pincodeChecked && pincodeDetails?.is_active;

  return {
    quantity,
    setQuantity,
    selectedImage,
    selectedSize,
    setSelectedSize,
    selectedColor,
    setSelectedColor,
    selectedMedia,
    setSelectedMedia,
    localLoading,
    pincode,
    setPincode,
    productDetails,
    unit,
    setUnit,
    imageRef,
    modalRef,
    loading,
    pinloading,
    pinerror,
    isPincodeLocked,
    currentPrice,
    handleMouseMove,
    handleMouseLeave,
    handleOutsideClick,
    handleCheck,
    handleReset,
    handleAddToCart,
    toggleWishlist,
    wishlistLoading,
    selectionColor,
    sizeSelection,
    handleClose,
    collection,
    pincodeDetails,
  };
}

export default useQuickView;